import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaInfoCircle, FaCalculator, FaMoneyBillWave, FaCalendarAlt, FaPercentage } from 'react-icons/fa';

const LoanCosts = () => {
  const [isLoanDetailsHovered, setIsLoanDetailsHovered] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            <span className="relative inline-block">
              How much will I pay back?
              <motion.span 
                //  
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: 0.7, duration: 0.5 }}
              />
            </span>
          </h1>
          <p className="mt-4 text-lg text-gray-700 max-w-3xl mx-auto">
            You will get a full payment schedule with the total amount of your repayments as well as a description of all other fees before receiving your loan. However here is a simple example:
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Loan Information Card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="md:col-span-3 bg-white rounded-xl shadow-lg overflow-hidden"
          >
            <div className="bg-blue-900 text-white p-6">
              <h2 className="text-2xl font-bold flex items-center">
                <FaCalculator className="mr-3" /> Loan Example
              </h2>
              <p className="mt-2 text-blue-100">Sample repayment calculation</p>
            </div>

            <motion.div 
              className="p-6"
              whileHover={{ scale: 1.02 }}
              onHoverStart={() => setIsLoanDetailsHovered(true)}
              onHoverEnd={() => setIsLoanDetailsHovered(false)}
              transition={{ duration: 0.3 }}
            >
              <div className="text-lg font-medium text-gray-900 mb-6">
                When you borrow an amount of $2,500 over 2 years, you will pay:
              </div>

              <div className="space-y-5">
                <div className="flex items-start p-4 border-l-4 border-blue-500 bg-blue-50 rounded-r-lg">
                  <FaMoneyBillWave className="text-blue-600 text-xl mt-1 mr-4" />
                  <div>
                    <div className="font-semibold text-gray-800">Statutory fee</div>
                    <div className="text-2xl font-bold text-blue-900">$400</div>
                  </div>
                </div>

                <div className="flex items-start p-4 border-l-4 border-green-500 bg-green-50 rounded-r-lg">
                  <FaPercentage className="text-green-600 text-xl mt-1 mr-4" />
                  <div>
                    <div className="font-semibold text-gray-800">Annual Cost Rate (Interest)</div>
                    <div className="text-2xl font-bold text-blue-900">47.6158%</div>
                  </div>
                </div>

                <div className="flex items-start p-4 border-l-4 border-yellow-500 bg-yellow-50 rounded-r-lg">
                  <FaInfoCircle className="text-yellow-600 text-xl mt-1 mr-4" />
                  <div>
                    <div className="font-semibold text-gray-800">Comparison rate</div>
                    <div className="text-2xl font-bold text-blue-900">65.6597% pa*</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Repayment Visualization */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="md:col-span-2 flex flex-col"
          >
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 flex-grow">
              <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center">
                <FaCalendarAlt className="mr-2 text-green-500" /> Repayment Summary
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <span className="font-medium text-gray-600">Loan amount</span>
                  <span className="font-bold text-gray-900">$2,500</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <span className="font-medium text-gray-600">Loan term</span>
                  <span className="font-bold text-gray-900">24 months</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <span className="font-medium text-gray-600">Fees</span>
                  <span className="font-bold text-gray-900">$400</span>
                </div>
                <div className="flex justify-between border-b border-gray-200 pb-3">
                  <span className="font-medium text-gray-600">Interest charges</span>
                  <motion.span 
                    className="font-bold text-gray-900"
                    animate={{ scale: isLoanDetailsHovered ? [1, 1.05, 1] : 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    ~$1,243
                  </motion.span>
                </div>
                <div className="flex justify-between pt-2">
                  <span className="font-semibold text-gray-800">Total repayable</span>
                  <motion.span 
                    className="font-bold text-xl text-blue-900"
                    animate={{ scale: isLoanDetailsHovered ? [1, 1.1, 1] : 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    ~$4,143
                  </motion.span>
                </div>
              </div>
            </div>

            <a 
              href="/apply-now" 
              className="block"
            >
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg shadow-lg transition duration-300 text-center"
              >
                APPLY NOW!
              </motion.button>
            </a>
          </motion.div>
        </div>

        {/* Warning Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-12 p-6 border border-yellow-200 rounded-lg bg-yellow-50"
        >
          <div className="flex items-start">
            <FaInfoCircle className="text-yellow-600 text-xl mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-gray-800 mb-2">WARNING:</h3>
              <p className="text-gray-700">
                This comparison rate is true only for the example given and may not include all fees and charges. Different terms, fees or other loan amounts might result in a different comparison rate.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Interactive Calculator (Optional) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600 italic">
            The above is just an example. For personalized loan calculations, please <a href="/contact-us" className="text-blue-600 hover:underline">contact our team</a>.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LoanCosts; 